
import React, { useState, useCallback } from 'react'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'
import type { ValidationErrors } from '@/features/enrollment'
import { EnrollmentApi } from '@/features/enrollment/services/enrollment.service'
import { CheckCircle2, AlertCircle, Loader2, ArrowLeft, ArrowRight } from 'lucide-react'

interface ComplianceStepProps {
    enrollmentData: any
    updateEnrollmentData: (field: string, value: any) => void
    handleNavigate: (targetIndex: number) => void | Promise<void>
    handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
    isNavigating: boolean
    currentStepIndex?: number
    totalSteps?: number
    onComplete: () => void
    sessionId?: string
    [key: string]: any
}

export default function ComplianceStep({
    enrollmentData,
    updateEnrollmentData,
    handleNavigate,
    handleNavigateWithCompletion,
    isNavigating,
    currentStepIndex,
    totalSteps = 6,
    onComplete,
    sessionId
}: ComplianceStepProps) {
    const [errors, setErrors] = useState<ValidationErrors>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [isSubmitted, setIsSubmitted] = useState(false)

    const handleSubmit = useCallback(async () => {
        const validationErrors: ValidationErrors = {}

        if (!enrollmentData.cmsAuthorizationOption) {
            validationErrors.cmsAuthorizationOption = 'Please select CMS authorization status'
        }

        if (enrollmentData.authorizedUserConsent !== 'CMS Call Was Completed And Client Gave Authorization') {
            validationErrors.authorizedUserConsent = 'CMS call authorization is required'
        }
        if (enrollmentData.permissionGiven !== 'Yes') {
            validationErrors.permissionGiven = 'Permission must be granted'
        }
        if (!enrollmentData.hasInsurance) {
            validationErrors.hasInsurance = 'Please select insurance enrollment status'
        }
        if (enrollmentData.termsAgreement !== 'YES') {
            validationErrors.termsAgreement = 'You must agree to the terms and conditions'
        }

        setErrors(validationErrors)

        if (Object.keys(validationErrors).length === 0) {
            try {
                setIsSubmitting(true)
                setSubmitError(null)

                console.log('Submitting to JotForm...', enrollmentData)
                if (!sessionId) {
                    throw new Error('Session ID is missing')
                }
                await EnrollmentApi.submitACAForm(sessionId, enrollmentData)
                console.log('JotForm submission successful')

                setIsSubmitted(true)

                // Auto-navigate to Step 6 (Follow-Up) after successful submission
                setTimeout(() => {
                    if (handleNavigateWithCompletion && currentStepIndex !== undefined) {
                        handleNavigateWithCompletion(currentStepIndex + 1)
                    } else {
                        handleNavigate(5) // Go to step 6 (index 5)
                    }
                }, 1500)

            } catch (error) {
                console.error('JotForm submission failed:', error)
                setSubmitError(error instanceof Error ? error.message : 'Failed to submit. Please try again.')
            } finally {
                setIsSubmitting(false)
            }
        }
    }, [enrollmentData, sessionId, handleNavigate, handleNavigateWithCompletion, currentStepIndex])

    const handleBack = useCallback(() => {
        if (currentStepIndex !== undefined && currentStepIndex > 0) {
            handleNavigate(currentStepIndex - 1)
        }
    }, [currentStepIndex, handleNavigate])

    return (
        <StageWrapper
            currentStepIndex={currentStepIndex ?? 4}
            totalSteps={totalSteps}
            handleNavigate={handleNavigate}
            handleNavigateWithCompletion={handleNavigateWithCompletion}
            isNavigating={isNavigating}
            showNextButton={false}
            customButtons={
                <div className="flex justify-between">
                    <button
                        onClick={handleBack}
                        disabled={isNavigating || isSubmitting}
                        className="btn-outline flex items-center space-x-2 min-w-[100px] justify-center"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back</span>
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isNavigating || isSubmitting || isSubmitted}
                        className="btn-primary flex items-center space-x-2 min-w-[200px] justify-center"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Submitting...</span>
                            </>
                        ) : isSubmitted ? (
                            <>
                                <CheckCircle2 className="w-4 h-4" />
                                <span>Proceeding to Follow-Up...</span>
                            </>
                        ) : (
                            <>
                                <span>Complete Enrollment</span>
                                <ArrowRight className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </div>
            }
        >
            <div className="card text-left">
                {/* Submission Status */}
                {isSubmitting && (
                    <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center space-x-3 animate-pulse">
                        <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
                        <span className="text-indigo-700 font-medium">Submitting enrollment...</span>
                    </div>
                )}

                {submitError && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div className="flex-1">
                            <span className="text-red-700 font-bold block mb-1">Submission Failed</span>
                            <span className="text-red-600 text-sm">{submitError}</span>
                            <p className="mt-2 text-xs text-red-500 italic">Click "Complete Enrollment" to retry.</p>
                        </div>
                    </div>
                )}

                {isSubmitted && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="text-green-700 font-medium">Enrollment submitted! Redirecting to Follow-Up...</span>
                    </div>
                )}

                <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-accent-900">Step 5: Compliance & Authorization</h3>
                        <p className="text-sm text-accent-600">Final CMS authorization and compliance verification.</p>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* CMS Authorization Option */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4">CMS Authorization Status</h4>
                        <div className="space-y-3">
                            <label className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                <input
                                    type="radio"
                                    name="cmsAuthorizationOption"
                                    value="completed"
                                    checked={enrollmentData.cmsAuthorizationOption === 'completed'}
                                    onChange={() => updateEnrollmentData('cmsAuthorizationOption', 'completed')}
                                    className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                />
                                <div className="ml-3">
                                    <span className="font-medium text-gray-900">Client has completed CMS authorization</span>
                                    <p className="text-sm text-gray-500">Recommended - The client has already called CMS and authorized the agent.</p>
                                </div>
                            </label>

                            <label className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors border-yellow-200 bg-yellow-50">
                                <input
                                    type="radio"
                                    name="cmsAuthorizationOption"
                                    value="later"
                                    checked={enrollmentData.cmsAuthorizationOption === 'later'}
                                    onChange={() => updateEnrollmentData('cmsAuthorizationOption', 'later')}
                                    className="mt-1 h-4 w-4 text-yellow-600 border-gray-300 focus:ring-yellow-500"
                                />
                                <div className="ml-3">
                                    <span className="font-medium text-yellow-800">Client will call later and setup authorization</span>
                                    <p className="text-sm text-yellow-700">(Not recommended) - Enrollment may be delayed until authorization is complete.</p>
                                </div>
                            </label>
                        </div>
                        {errors.cmsAuthorizationOption && <p className="mt-2 text-sm text-red-600">{errors.cmsAuthorizationOption}</p>}
                    </div>

                    {/* CMS Call Compliance */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4">CMS Hotline Verification</h4>
                        <div className="p-4 rounded-md border border-gray-200 text-sm text-gray-800 mb-4">
                            <p className="font-bold mb-2">COMPLIANCE: Call CMS Hotline 800-318-2596 With The Customer On The Phone and Say:</p>
                            <p className="font-medium text-lg leading-relaxed text-gray-900 border-l-4 border-gray-300 pl-4 py-2 my-4 italic">
                                "The customer gives approval to Health Connect Marketing and its licensed agents to be the Authorized User & Authorized Agent to speak on the client's behalf for 365 Days."
                            </p>
                            <p className="italic font-medium">Make sure CMS spells the Agent's name correctly.</p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Authorized User Consent For 365 Days *
                                </label>
                                <div className="space-y-2">
                                    {[
                                        'CMS Call Was Completed And Client Gave Authorization',
                                        'CMS CALL Not Completed'
                                    ].map(opt => (
                                        <div key={opt} className="flex items-center">
                                            <input
                                                type="radio"
                                                name="authorizedUserConsent"
                                                value={opt}
                                                checked={enrollmentData.authorizedUserConsent === opt}
                                                onChange={(e) => updateEnrollmentData('authorizedUserConsent', e.target.value)}
                                                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                            />
                                            <label className="ml-3 block text-sm font-medium text-gray-700">{opt}</label>
                                        </div>
                                    ))}
                                </div>
                                {errors.authorizedUserConsent && <p className="mt-1 text-sm text-red-600">{errors.authorizedUserConsent}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    I give Health Connect Marketing and its Licensed Agents permission to be my authorized user for 365 days. *
                                </label>
                                <div className="flex space-x-6">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="permissionGiven"
                                            value="Yes"
                                            checked={enrollmentData.permissionGiven === 'Yes'}
                                            onChange={() => updateEnrollmentData('permissionGiven', 'Yes')}
                                            className="form-radio h-5 w-5 text-indigo-600"
                                        />
                                        <span className="ml-2 text-gray-700">Yes</span>
                                    </label>
                                </div>
                                {errors.permissionGiven && <p className="mt-1 text-sm text-red-600">{errors.permissionGiven}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Insurance Enrollment Status *
                                </label>
                                <div className="space-y-2">
                                    {[
                                        'No, The Client Needs To Be Enrolled By A Licensed Agent',
                                        'Yes, I Completed Insurance Myself With The CMS PHONE CALL'
                                    ].map(opt => (
                                        <div key={opt} className="flex items-center">
                                            <input
                                                type="radio"
                                                name="hasInsurance"
                                                value={opt}
                                                checked={enrollmentData.hasInsurance === opt}
                                                onChange={(e) => updateEnrollmentData('hasInsurance', e.target.value)}
                                                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                            />
                                            <label className="ml-3 block text-sm font-medium text-gray-700">{opt}</label>
                                        </div>
                                    ))}
                                </div>
                                {errors.hasInsurance && <p className="mt-1 text-sm text-red-600">{errors.hasInsurance}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    I agree to the terms and conditions of this enrollment. *
                                </label>
                                <div className="flex space-x-6">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="termsAgreement"
                                            value="YES"
                                            checked={enrollmentData.termsAgreement === 'YES'}
                                            onChange={() => updateEnrollmentData('termsAgreement', 'YES')}
                                            className="form-radio h-5 w-5 text-indigo-600"
                                        />
                                        <span className="ml-2 text-gray-700">YES</span>
                                    </label>
                                </div>
                                {errors.termsAgreement && <p className="mt-1 text-sm text-red-600">{errors.termsAgreement}</p>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </StageWrapper>
    )
}
